import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The class-name joiner every primitive in this folder uses.
 *
 * It is not just `clsx`. `twMerge` resolves *conflicting* Tailwind utilities by keeping
 * the last one, which is what lets a caller pass `className="tw-bg-white"` and actually
 * override a variant's `tw-bg-brand` instead of ending up with both and depending on the
 * order they happen to appear in the compiled stylesheet. That matters more here than in
 * a normal Tailwind app, because `important: true` means a losing utility still carries
 * `!important` and would otherwise be unbeatable.
 *
 * Two pieces of configuration, both of which fail silently if you leave them out:
 *
 *   - **The prefix.** Without it every class looks unknown and merging quietly stops.
 *     Note that this is tailwind-merge v2's option, matching Tailwind v3. v3 of the
 *     library expects Tailwind v4's `tw:` syntax and accepts `prefix: 'tw-'` while doing
 *     nothing with it, which is a very quiet way to lose every conflict resolution.
 *
 *   - **The custom scales from tailwind.config.js.** `tw-text-body` is a font size, but
 *     tailwind-merge has no way to know that and files it as a text *colour*, so
 *     `cn('tw-text-body', 'tw-text-white')` drops the size. Same for every custom
 *     radius name.
 *     They have to be declared here or the type scale silently disappears from every
 *     component that also sets a colour.
 *
 *     The layer names and the named sizes go here for the opposite reason. None of them is
 *     a number, so tailwind-merge does not recognise them as a z-index, a width or a
 *     shadow and keeps both sides of a conflict, and which one wins is then down to the
 *     order Tailwind happens to emit them in. The named spacing steps are declared once,
 *     as theme spacing, which reaches every padding, margin and gap group at once.
 */
export const cn = extendTailwindMerge({
  prefix: 'tw-',
  extend: {
    theme: {
      spacing: [
        'control-y',
        'control-x',
        'label',
        'toolbar',
        'form-x',
        'form-y',
        'section',
        'index',
        'cell-y',
      ],
    },
    classGroups: {
      'font-size': [{ text: ['caption', 'small', 'body', 'section', 'title'] }],
      rounded: [
        { rounded: ['chip', 'tab', 'control', 'track', 'card', 'dialog', 'pill', 'check'] },
      ],
      z: [{ z: ['dialog', 'menu'] }],
      'min-w': [{ 'min-w': ['menu', 'control-sm', 'filter', 'col-primary'] }],
      'max-w': [{ 'max-w': ['dialog', 'dialog-sm', 'dialog-lg', 'dialog-xl', 'form'] }],
      h: [
        {
          h: [
            'dialog-full',
            'control',
            'control-sm',
            'input',
            'menu',
            'pill',
            'dot',
            'chip',
            'switch',
            'knob',
            'check',
            'action-bar',
          ],
        },
      ],
      w: [
        {
          w: [
            'dialog-full',
            'dot',
            'switch',
            'knob',
            'check',
            'filter',
            'filter-search',
            'index',
            'col-primary',
          ],
        },
      ],
      shadow: [{ shadow: ['switch', 'switch-active', 'bar', 'sticky-start', 'sticky-end'] }],
      // The native select's chevron is a background *image*. Unnamed, tailwind-merge
      // filed it as a background colour, so it cancelled `tw-bg-white` and was itself
      // cancelled by the position class after it: every shared Select lost its chevron.
      'bg-image': [{ bg: ['select-chevron'] }],
    },
  },
});

export default (...inputs) => cn(clsx(inputs));
